/**
 * Contracts API service using BaseApi pattern
 * Reduced from 164 lines to ~45 lines (73% reduction)
 */

import {
  PaginationParams,
  PaginatedResponse,
  Contract,
  CreateContract,
  UpdateContract,
  ServiceRetainerPlan,
  ServiceRetainerPlanGroup,
  CreateServiceRetainerPlanInput,
  CreateServiceRetainerPlanGroupInput,
  UpdateServiceRetainerPlanInput,
  UpdateServiceRetainerPlanGroupInput,
  CreateServiceRetainerPlanVersionInput,
  RetainerOverview,
  RetainerPeriod,
  RetainerTerms,
  RetainerPlanChange,
  ScheduleRetainerPlanChangeInput,
  CreateRetainerAdjustmentInput,
  RetainerDraftInvoiceResponse,
  RetainerPlanChangePreview,
  RealignRetainerPeriodsResult,
  RetainerSummary,
  EligibleRetainer,
  ContractChangeOrder,
  ContractChangeOrderSummary,
  CreateContractChangeOrderInput,
  UpdateContractChangeOrderInput,
  ContractBillingSchedulePreview,
  GenerateContractBillingSchedule,
  ChangeOrderBudgetOffer,
  ContractFundingCoverage,
} from '@scrambl/shared';
import { BaseApi } from './base-api';
import { createSingletonApi } from './singleton-factory';

class ContractApiService extends BaseApi<Contract, CreateContract, UpdateContract> {
  constructor() {
    super({
      baseEndpoint: '/contracts',
      responseNormalization: true
    });
  }

  // Override create to add deletedAt field required by backend
  async create(data: CreateContract): Promise<Contract> {
    const contractData = {
      ...data,
      deletedAt: null,
    };

    return super.create(contractData as CreateContract);
  }

  // Alias methods for backward compatibility
  async getContracts(params: Partial<PaginationParams> = {}): Promise<PaginatedResponse<Contract>> {
    return this.list(params);
  }

  async getContract(id: string): Promise<Contract> {
    return this.findById(id);
  }

  async createContract(data: CreateContract): Promise<Contract> {
    return this.create(data);
  }

  async updateContract(id: string, data: UpdateContract): Promise<Contract> {
    return this.update(id, data);
  }

  async deleteContract(id: string): Promise<void> {
    return this.delete(id);
  }

  async bulkDeleteContracts(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(`${this.config.baseEndpoint}/bulk-delete`, {
      method: 'POST',
      body: { ids }
    });
  }

  // T26/U5: baseline vs current value — change-order journal
  async getChangeOrderSummary(id: string): Promise<ContractChangeOrderSummary> {
    return this.makeRequest<ContractChangeOrderSummary>(`${this.config.baseEndpoint}/${id}/change-orders`);
  }

  async createChangeOrder(
    id: string,
    data: CreateContractChangeOrderInput,
  ): Promise<{
    contract: Contract;
    changeOrder: ContractChangeOrder;
    budgetOffer: ChangeOrderBudgetOffer | null;
  }> {
    return this.makeRequest<{
      contract: Contract;
      changeOrder: ContractChangeOrder;
      budgetOffer: ChangeOrderBudgetOffer | null;
    }>(
      `${this.config.baseEndpoint}/${id}/change-orders`,
      { method: 'POST', body: data },
    );
  }

  // Phase 2 budgeting rework: contract value decomposed into allocated-to-budgets
  // (funding links) and scheduled-to-billing (schedule lines), side by side.
  async getFundingCoverage(id: string): Promise<ContractFundingCoverage> {
    return this.makeRequest<ContractFundingCoverage>(
      `${this.config.baseEndpoint}/${id}/funding-coverage`,
    );
  }

  async updateChangeOrder(
    id: string,
    changeOrderId: string,
    data: UpdateContractChangeOrderInput,
  ): Promise<ContractChangeOrder> {
    return this.makeRequest<ContractChangeOrder>(
      `${this.config.baseEndpoint}/${id}/change-orders/${changeOrderId}`,
      { method: 'PUT', body: data },
    );
  }

  // Materialize recurring billing schedule lines from the contract's billing
  // terms. commit=false returns a dry-run diff; commit=true applies it.
  async generateBillingSchedule(
    id: string,
    data: GenerateContractBillingSchedule,
  ): Promise<ContractBillingSchedulePreview> {
    return this.makeRequest<ContractBillingSchedulePreview>(
      `${this.config.baseEndpoint}/${id}/generate-billing-schedule`,
      { method: 'POST', body: data },
    );
  }

  async getRetainer(id: string, period?: string): Promise<RetainerOverview> {
    const query = period ? `?period=${encodeURIComponent(period)}` : '';
    return this.makeRequest<RetainerOverview>(`${this.config.baseEndpoint}/${id}/retainer${query}`);
  }

  async updateRetainer(id: string, data: RetainerTerms) {
    return this.makeRequest(`${this.config.baseEndpoint}/${id}/retainer`, {
      method: 'PUT',
      body: data,
    });
  }

  // Re-anchor existing open/upcoming periods onto the contract's current
  // start-date anniversary windows (offered after the start date changes).
  async realignRetainerPeriods(id: string): Promise<RealignRetainerPeriodsResult> {
    return this.makeRequest<RealignRetainerPeriodsResult>(
      `${this.config.baseEndpoint}/${id}/retainer/realign-periods`,
      { method: 'POST' },
    );
  }

  async listRetainerPeriods(id: string): Promise<RetainerPeriod[]> {
    return this.makeRequest<RetainerPeriod[]>(`${this.config.baseEndpoint}/${id}/retainer-periods`);
  }

  async listRetainerPlanChanges(id: string): Promise<RetainerPlanChange[]> {
    return this.makeRequest<RetainerPlanChange[]>(`${this.config.baseEndpoint}/${id}/retainer-plan-changes`);
  }

  async scheduleRetainerPlanChange(id: string, data: ScheduleRetainerPlanChangeInput): Promise<RetainerPlanChange> {
    return this.makeRequest<RetainerPlanChange>(`${this.config.baseEndpoint}/${id}/retainer-plan-changes`, {
      method: 'POST',
      body: data,
    });
  }

  async previewRetainerPlanChange(id: string, data: ScheduleRetainerPlanChangeInput): Promise<RetainerPlanChangePreview> {
    return this.makeRequest<RetainerPlanChangePreview>(`${this.config.baseEndpoint}/${id}/retainer-plan-changes/preview`, {
      method: 'POST',
      body: data,
    });
  }

  async reopenRetainerPeriod(id: string, periodId: string): Promise<RetainerPeriod> {
    return this.makeRequest<RetainerPeriod>(`${this.config.baseEndpoint}/${id}/retainer-periods/${periodId}/reopen`, {
      method: 'POST',
    });
  }

  async cancelRetainerPlanChange(id: string, changeId: string): Promise<RetainerPlanChange> {
    return this.makeRequest<RetainerPlanChange>(`${this.config.baseEndpoint}/${id}/retainer-plan-changes/${changeId}/cancel`, {
      method: 'POST',
    });
  }

  async recalculateRetainerPeriod(id: string, periodId: string): Promise<RetainerPeriod> {
    return this.makeRequest<RetainerPeriod>(`${this.config.baseEndpoint}/${id}/retainer-periods/${periodId}/recalculate`, {
      method: 'POST',
    });
  }

  async closeRetainerPeriod(id: string, periodId: string): Promise<RetainerPeriod> {
    return this.makeRequest<RetainerPeriod>(`${this.config.baseEndpoint}/${id}/retainer-periods/${periodId}/close`, {
      method: 'POST',
    });
  }

  async addRetainerAdjustment(id: string, periodId: string, data: CreateRetainerAdjustmentInput): Promise<RetainerPeriod> {
    return this.makeRequest<RetainerPeriod>(`${this.config.baseEndpoint}/${id}/retainer-periods/${periodId}/adjustments`, {
      method: 'POST',
      body: data,
    });
  }

  async createRetainerDraftInvoices(id: string, periodId: string): Promise<RetainerDraftInvoiceResponse> {
    return this.makeRequest<RetainerDraftInvoiceResponse>(`${this.config.baseEndpoint}/${id}/retainer-periods/${periodId}/draft-invoices`, {
      method: 'POST',
    });
  }

  async getEligibleRetainerForModule(moduleType: string, moduleId: string): Promise<EligibleRetainer | null> {
    const params = new URLSearchParams({ moduleType, moduleId });
    return this.makeRequest<EligibleRetainer | null>(`${this.config.baseEndpoint}/eligible-retainer?${params.toString()}`);
  }

  async listRetainerSummaries(params?: { entityId?: string }): Promise<RetainerSummary[]> {
    const query = params?.entityId ? `?${new URLSearchParams({ entityId: params.entityId }).toString()}` : '';
    return this.makeRequest<RetainerSummary[]>(`${this.config.baseEndpoint}/retainer-summaries${query}`);
  }

  async listServiceRetainerPlans(includeArchived = false): Promise<ServiceRetainerPlan[]> {
    const query = includeArchived ? '?includeArchived=true' : '';
    return this.makeRequest<ServiceRetainerPlan[]>(`${this.config.baseEndpoint}/settings/service-retainer-plans${query}`);
  }

  async createServiceRetainerPlan(data: CreateServiceRetainerPlanInput): Promise<ServiceRetainerPlan> {
    return this.makeRequest<ServiceRetainerPlan>(`${this.config.baseEndpoint}/settings/service-retainer-plans`, {
      method: 'POST',
      body: data,
    });
  }

  async updateServiceRetainerPlan(id: string, data: UpdateServiceRetainerPlanInput): Promise<ServiceRetainerPlan> {
    return this.makeRequest<ServiceRetainerPlan>(`${this.config.baseEndpoint}/settings/service-retainer-plans/${id}`, {
      method: 'PUT',
      body: data,
    });
  }

  async archiveServiceRetainerPlan(id: string): Promise<ServiceRetainerPlan> {
    return this.makeRequest<ServiceRetainerPlan>(`${this.config.baseEndpoint}/settings/service-retainer-plans/${id}/archive`, {
      method: 'POST',
    });
  }

  async duplicateServiceRetainerPlan(id: string, name?: string): Promise<ServiceRetainerPlan> {
    return this.makeRequest<ServiceRetainerPlan>(`${this.config.baseEndpoint}/settings/service-retainer-plans/${id}/duplicate`, {
      method: 'POST',
      body: { name },
    });
  }

  async moveServiceRetainerPlan(id: string, direction: 'UP' | 'DOWN'): Promise<ServiceRetainerPlan[]> {
    return this.makeRequest<ServiceRetainerPlan[]>(`${this.config.baseEndpoint}/settings/service-retainer-plans/${id}/move`, {
      method: 'POST',
      body: { direction },
    });
  }

  async listServiceRetainerPlanGroups(includeArchived = false): Promise<ServiceRetainerPlanGroup[]> {
    const query = includeArchived ? '?includeArchived=true' : '';
    return this.makeRequest<ServiceRetainerPlanGroup[]>(`${this.config.baseEndpoint}/settings/service-retainer-plan-groups${query}`);
  }

  async createServiceRetainerPlanGroup(data: CreateServiceRetainerPlanGroupInput): Promise<ServiceRetainerPlanGroup> {
    return this.makeRequest<ServiceRetainerPlanGroup>(`${this.config.baseEndpoint}/settings/service-retainer-plan-groups`, {
      method: 'POST',
      body: data,
    });
  }

  async updateServiceRetainerPlanGroup(id: string, data: UpdateServiceRetainerPlanGroupInput): Promise<ServiceRetainerPlanGroup> {
    return this.makeRequest<ServiceRetainerPlanGroup>(`${this.config.baseEndpoint}/settings/service-retainer-plan-groups/${id}`, {
      method: 'PUT',
      body: data,
    });
  }

  async archiveServiceRetainerPlanGroup(id: string): Promise<ServiceRetainerPlanGroup> {
    return this.makeRequest<ServiceRetainerPlanGroup>(`${this.config.baseEndpoint}/settings/service-retainer-plan-groups/${id}/archive`, {
      method: 'POST',
    });
  }

  async moveServiceRetainerPlanGroup(id: string, direction: 'UP' | 'DOWN'): Promise<ServiceRetainerPlanGroup[]> {
    return this.makeRequest<ServiceRetainerPlanGroup[]>(`${this.config.baseEndpoint}/settings/service-retainer-plan-groups/${id}/move`, {
      method: 'POST',
      body: { direction },
    });
  }

  async deleteServiceRetainerPlanGroup(id: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/settings/service-retainer-plan-groups/${id}`, {
      method: 'DELETE',
    });
  }

  async createServiceRetainerPlanVersion(id: string, data: CreateServiceRetainerPlanVersionInput): Promise<ServiceRetainerPlan> {
    return this.makeRequest<ServiceRetainerPlan>(`${this.config.baseEndpoint}/settings/service-retainer-plans/${id}/versions`, {
      method: 'POST',
      body: data,
    });
  }
}

// Export singleton instance using factory
export const contractApi = createSingletonApi(ContractApiService);

// Export class for backward compatibility
export { ContractApiService as ContractApi };
